// store/slices/teamSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { 
  TeamMember, 
  Team, 
  Role, 
  TeamState, 
  ApiResponse, 
  PaginationParams 
} from '../../types/team.types';

// ✅ Use existing service instead of creating new API class
import * as teamService from '../../services/team.service';
import logger from '../../utils/logger';

logger.log('🏗️ TeamSlice: Initializing with existing team.service.ts');

const initialState: TeamState = {
  members: [],
  teams: [],
  roles: [],
  loading: {
    members: false,
    teams: false,
    roles: false,
    creating: false,
    updating: false,
    deleting: false,
  },
  error: null,
  pagination: {
    members: { page: 1, perPage: 50, total: 0 },
    teams: { page: 1, perPage: 50, total: 0 },
  },
};

// ✅ REMOVED: TeamApiService class (was using dummy URLs)
// ✅ NOW USING: Existing team.service.ts methods

// Async Thunks - Using existing service methods
export const fetchTeamMembers = createAsyncThunk(
  'team/fetchMembers',
  async (params: PaginationParams = {}) => {
    logger.log('🔄 TeamSlice: Fetching team members with params:', params);

    try {
      // Convert PaginationParams to match team.service.ts expected format
      const requestParams: any = {
        page: params.page || 1,
        perPage: params.perPage || 50,
      };

      // Pass through search and filter parameters
      if (params.search) {
        requestParams.search = params.search;
      }
      if (params.teams) {
        requestParams.teams = params.teams; // Fixed: API expects 'teams' not 'team'
      }
      if (params.orderBy) {
        requestParams.orderBy = params.orderBy;
      }
      if (params.isAscending !== undefined) {
        requestParams.isAscending = params.isAscending;
      }

      logger.log('🔄 TeamSlice: Calling teamService.getAllTeamMembers with:', requestParams);

      // ✅ Use existing service method
      const response = await teamService.getAllTeamMembers(requestParams);

      logger.log('✅ TeamSlice: Raw axios response:', response);
      logger.log('✅ TeamSlice: Response data:', response.data);

      // ✅ Return axios response data (your service returns axios response objects)
      return response.data;

    } catch (error) {
      console.error('❌ TeamSlice: Error fetching team members:', error);
      throw error; // Re-throw to trigger rejected case
    }
  }
);

export const fetchTeams = createAsyncThunk(
  'team/fetchTeams',
  async (params: PaginationParams = {}) => {
    logger.log('🔄 TeamSlice: Fetching teams with params:', params);

    try {
      // Convert PaginationParams to match team.service.ts expected format
      const requestParams: any = {
        page: params.page || 1,
        perPage: params.perPage || 50
      };

      // Pass through search parameters
      if (params.search) {
        requestParams.search = params.search;
      }
      if (params.orderBy) {
        requestParams.orderBy = params.orderBy;
      }

      logger.log('🔄 TeamSlice: Calling teamService.getAllTeams with:', requestParams);

      // ✅ Use existing service method
      const response = await teamService.getAllTeams(requestParams);

      logger.log('✅ TeamSlice: Raw axios response:', response);
      logger.log('✅ TeamSlice: Response data:', response.data);

      // ✅ Return axios response data
      return response.data;

    } catch (error) {
      console.error('❌ TeamSlice: Error fetching teams:', error);
      throw error;
    }
  }
);

export const fetchRoles = createAsyncThunk(
  'team/fetchRoles',
  async () => {
    logger.log('🔄 TeamSlice: Fetching roles...');
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.getAllRoles');
      
      // ✅ Use existing service method
      const response = await teamService.getAllRoles();
      
      logger.log('✅ TeamSlice: Raw axios response:', response);
      logger.log('✅ TeamSlice: Response data:', response.data);
      
      // ✅ Return axios response data
      return response.data;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error fetching roles:', error);
      throw error;
    }
  }
);

export const createTeamMember = createAsyncThunk(
  'team/createMember',
  async (member: Partial<TeamMember>) => {
    logger.log('🔄 TeamSlice: Creating team member with data:', member);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.createTeamMember');
      
      // ✅ Use existing service method
      const response = await teamService.createTeamMember(member);
      
      logger.log('✅ TeamSlice: Member created - Raw response:', response);
      logger.log('✅ TeamSlice: Member created - Response data:', response.data);
      
      // ✅ Return axios response data
      return response.data;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error creating team member:', error);
      throw error;
    }
  }
);

export const updateTeamMember = createAsyncThunk(
  'team/updateMember',
  async (member: Partial<TeamMember>) => {
    logger.log('🔄 TeamSlice: Updating team member with data:', member);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.updateTeamMember');
      
      // ✅ Use existing service method
      const response = await teamService.updateTeamMember(member);
      
      logger.log('✅ TeamSlice: Member updated - Raw response:', response);
      logger.log('✅ TeamSlice: Member updated - Response data:', response.data);
      
      // ✅ Return axios response data
      return response.data;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error updating team member:', error);
      throw error;
    }
  }
);

export const createTeam = createAsyncThunk(
  'team/createTeam',
  async (team: Partial<Team>) => {
    logger.log('🔄 TeamSlice: Creating team with data:', team);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.createTeam');
      
      // ✅ Use existing service method
      const response = await teamService.createTeam(team);
      
      logger.log('✅ TeamSlice: Team created - Raw response:', response);
      logger.log('✅ TeamSlice: Team created - Response data:', response.data);
      
      // ✅ Return axios response data
      return response.data;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error creating team:', error);
      throw error;
    }
  }
);

export const updateTeam = createAsyncThunk(
  'team/updateTeam',
  async (team: Partial<Team>) => {
    logger.log('🔄 TeamSlice: Updating team with data:', team);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.updateTeam');
      
      // ✅ Use existing service method
      const response = await teamService.updateTeam(team);
      
      logger.log('✅ TeamSlice: Team updated - Raw response:', response);
      logger.log('✅ TeamSlice: Team updated - Response data:', response.data);
      
      // ✅ Return axios response data
      return response.data;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error updating team:', error);
      throw error;
    }
  }
);

export const deleteTeam = createAsyncThunk(
  'team/deleteTeam',
  async (teamId: string) => {
    logger.log('🔄 TeamSlice: Deleting team with ID:', teamId);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.deleteTeam');
      
      // ✅ Use existing service method
      await teamService.deleteTeam(teamId);
      
      logger.log('✅ TeamSlice: Team deleted successfully:', teamId);
      
      // ✅ Return the teamId for state update
      return teamId;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error deleting team:', error);
      throw error;
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  'team/deleteEmployee',
  async (employeeId: string) => {
    logger.log('🔄 TeamSlice: Deleting employee with ID:', employeeId);
    
    try {
      logger.log('🔄 TeamSlice: Calling teamService.deleteTeamMember');
      
      // ✅ Use existing service method
      await teamService.deleteTeamMember(employeeId);
      
      logger.log('✅ TeamSlice: Employee deleted successfully:', employeeId);
      
      // ✅ Return the employeeId for state update
      return employeeId;
      
    } catch (error) {
      console.error('❌ TeamSlice: Error deleting employee:', error);
      throw error;
    }
  }
);

// Slice - No changes to reducers, just improved logging
const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    // Synchronous actions
    clearError: (state) => {
      logger.log('🧹 TeamSlice: Clearing error');
      state.error = null;
    },
    setMembersPage: (state, action: PayloadAction<number>) => {
      logger.log('📄 TeamSlice: Setting members page to:', action.payload);
      state.pagination.members.page = action.payload;
    },
    setTeamsPage: (state, action: PayloadAction<number>) => {
      logger.log('📄 TeamSlice: Setting teams page to:', action.payload);
      state.pagination.teams.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Members
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        logger.log('⏳ TeamSlice: fetchTeamMembers pending');
        state.loading.members = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: fetchTeamMembers fulfilled with data:', action.payload);
        state.loading.members = false;
        
        // ✅ Handle different response formats safely
        const response = action.payload;
        if (Array.isArray(response)) {
          // Direct array response
          state.members = response;
          state.pagination.members.total = response.length;
          logger.log('📊 TeamSlice: Set members from direct array, count:', response.length);
        } else if (response && typeof response === 'object') {
          // Object response with data property
          state.members = response.data || response.members || [];
          state.pagination.members.total = response.total || response.count || state.members.length;
          logger.log('📊 TeamSlice: Set members from object response, count:', state.members.length);
        } else {
          // Fallback
          state.members = [];
          state.pagination.members.total = 0;
          logger.log('⚠️ TeamSlice: Unexpected response format, set empty array');
        }
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        console.error('❌ TeamSlice: fetchTeamMembers rejected:', action.error);
        state.loading.members = false;
        state.error = action.error.message || 'Failed to fetch team members';
      });

    // Fetch Teams
    builder
      .addCase(fetchTeams.pending, (state) => {
        logger.log('⏳ TeamSlice: fetchTeams pending');
        state.loading.teams = true;
        state.error = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: fetchTeams fulfilled with data:', action.payload);
        state.loading.teams = false;
        
        // ✅ Handle different response formats safely
        const response = action.payload;
        if (Array.isArray(response)) {
          // Direct array response
          state.teams = response;
          state.pagination.teams.total = response.length;
          logger.log('📊 TeamSlice: Set teams from direct array, count:', response.length);
        } else if (response && typeof response === 'object') {
          // Object response with data property
          state.teams = response.data || response.teams || [];
          state.pagination.teams.total = response.total || response.count || state.teams.length;
          logger.log('📊 TeamSlice: Set teams from object response, count:', state.teams.length);
        } else {
          // Fallback
          state.teams = [];
          state.pagination.teams.total = 0;
          logger.log('⚠️ TeamSlice: Unexpected response format, set empty array');
        }
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        console.error('❌ TeamSlice: fetchTeams rejected:', action.error);
        state.loading.teams = false;
        state.error = action.error.message || 'Failed to fetch teams';
      });

    // Fetch Roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        logger.log('⏳ TeamSlice: fetchRoles pending');
        state.loading.roles = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: fetchRoles fulfilled with data:', action.payload);
        state.loading.roles = false;
        
        // ✅ Handle different response formats safely
        const response = action.payload;
        if (Array.isArray(response)) {
          state.roles = response;
          logger.log('📊 TeamSlice: Set roles from direct array, count:', response.length);
        } else if (response && typeof response === 'object') {
          state.roles = response.data || response.roles || [];
          logger.log('📊 TeamSlice: Set roles from object response, count:', state.roles.length);
        } else {
          state.roles = [];
          logger.log('⚠️ TeamSlice: Unexpected response format, set empty array');
        }
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        console.error('❌ TeamSlice: fetchRoles rejected:', action.error);
        state.loading.roles = false;
        state.error = action.error.message || 'Failed to fetch roles';
      });

    // Create Member
    builder
      .addCase(createTeamMember.pending, (state) => {
        logger.log('⏳ TeamSlice: createTeamMember pending');
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createTeamMember.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: createTeamMember fulfilled with data:', action.payload);
        state.loading.creating = false;
        
        // ✅ Handle response and add to state
        const response = action.payload;
        const newMember = response?.data || response;
        if (newMember && typeof newMember === 'object' && newMember._id) {
          state.members.push(newMember as TeamMember);
          logger.log('➕ TeamSlice: Added new member to state:', newMember._id);
        } else {
          logger.log('⚠️ TeamSlice: Created member but no valid data to add to state');
        }
      })
      .addCase(createTeamMember.rejected, (state, action) => {
        console.error('❌ TeamSlice: createTeamMember rejected:', action.error);
        state.loading.creating = false;
        state.error = action.error.message || 'Failed to create team member';
      });

    // Update Member
    builder
      .addCase(updateTeamMember.pending, (state) => {
        logger.log('⏳ TeamSlice: updateTeamMember pending');
        state.loading.updating = true;
        state.error = null;
      })
      .addCase(updateTeamMember.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: updateTeamMember fulfilled with data:', action.payload);
        state.loading.updating = false;
        
        // ✅ Handle response and update state
        const response = action.payload;
        const updatedMember = response?.data || response;
        if (updatedMember && typeof updatedMember === 'object' && updatedMember._id) {
          const index = state.members.findIndex(m => m._id === updatedMember._id);
          if (index !== -1) {
            state.members[index] = updatedMember as TeamMember;
            logger.log('🔄 TeamSlice: Updated member in state:', updatedMember._id);
          } else {
            logger.log('⚠️ TeamSlice: Member not found in state for update:', updatedMember._id);
          }
        } else {
          logger.log('⚠️ TeamSlice: Updated member but no valid data to update state');
        }
      })
      .addCase(updateTeamMember.rejected, (state, action) => {
        console.error('❌ TeamSlice: updateTeamMember rejected:', action.error);
        state.loading.updating = false;
        state.error = action.error.message || 'Failed to update team member';
      });

    // Create Team
    builder
      .addCase(createTeam.pending, (state) => {
        logger.log('⏳ TeamSlice: createTeam pending');
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: createTeam fulfilled with data:', action.payload);
        state.loading.creating = false;
        
        // ✅ Handle response and add to state
        const response = action.payload;
        const newTeam = response?.data || response;
        if (newTeam && typeof newTeam === 'object' && newTeam._id) {
          state.teams.push(newTeam as Team);
          logger.log('➕ TeamSlice: Added new team to state:', newTeam._id);
        } else {
          logger.log('⚠️ TeamSlice: Created team but no valid data to add to state');
        }
      })
      .addCase(createTeam.rejected, (state, action) => {
        console.error('❌ TeamSlice: createTeam rejected:', action.error);
        state.loading.creating = false;
        state.error = action.error.message || 'Failed to create team';
      });

    // Update Team
    builder
      .addCase(updateTeam.pending, (state) => {
        logger.log('⏳ TeamSlice: updateTeam pending');
        state.loading.updating = true;
        state.error = null;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: updateTeam fulfilled with data:', action.payload);
        state.loading.updating = false;
        
        // ✅ Handle response and update state
        const response = action.payload;
        const updatedTeam = response?.data || response;
        if (updatedTeam && typeof updatedTeam === 'object' && updatedTeam._id) {
          const index = state.teams.findIndex(t => t._id === updatedTeam._id);
          if (index !== -1) {
            state.teams[index] = updatedTeam as Team;
            logger.log('🔄 TeamSlice: Updated team in state:', updatedTeam._id);
          } else {
            logger.log('⚠️ TeamSlice: Team not found in state for update:', updatedTeam._id);
          }
        } else {
          logger.log('⚠️ TeamSlice: Updated team but no valid data to update state');
        }
      })
      .addCase(updateTeam.rejected, (state, action) => {
        console.error('❌ TeamSlice: updateTeam rejected:', action.error);
        state.loading.updating = false;
        state.error = action.error.message || 'Failed to update team';
      });

    // Delete Team
    builder
      .addCase(deleteTeam.pending, (state) => {
        logger.log('⏳ TeamSlice: deleteTeam pending');
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: deleteTeam fulfilled, removing ID:', action.payload);
        state.loading.deleting = false;
        state.teams = state.teams.filter(t => t._id !== action.payload);
        logger.log('🗑️ TeamSlice: Removed team from state:', action.payload);
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        console.error('❌ TeamSlice: deleteTeam rejected:', action.error);
        state.loading.deleting = false;
        state.error = action.error.message || 'Failed to delete team';
      });

    // Delete Employee
    builder
      .addCase(deleteEmployee.pending, (state) => {
        logger.log('⏳ TeamSlice: deleteEmployee pending');
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        logger.log('✅ TeamSlice: deleteEmployee fulfilled, removing ID:', action.payload);
        state.loading.deleting = false;
        state.members = state.members.filter(m => m._id !== action.payload);
        logger.log('🗑️ TeamSlice: Removed member from state:', action.payload);
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        console.error('❌ TeamSlice: deleteEmployee rejected:', action.error);
        state.loading.deleting = false;
        state.error = action.error.message || 'Failed to delete employee';
      });
  },
});

// Selectors - No changes, keeping all existing selectors
export const selectTeamMembers = (state: RootState): TeamMember[] => {
  const members = state.whatsapp.team?.members || [];
  logger.log('🔍 TeamSlice Selector: selectTeamMembers returning:', members.length, 'members');
  return members;
};

export const selectTeams = (state: RootState): Team[] => {
  const teams = state.whatsapp.team?.teams || [];
  logger.log('🔍 TeamSlice Selector: selectTeams returning:', teams.length, 'teams');
  return teams;
};

export const selectRoles = (state: RootState): Role[] => {
  const roles = state.whatsapp.team?.roles || [];
  logger.log('🔍 TeamSlice Selector: selectRoles returning:', roles.length, 'roles');
  return roles;
};

export const selectTeamLoading = (state: RootState) => 
  state.whatsapp.team?.loading;

export const selectTeamError = (state: RootState): string | null => 
  state.whatsapp.team?.error || null;

export const selectTeamPagination = (state: RootState) => 
  state.whatsapp.team?.pagination;

// Derived selectors (following mobile patterns)
export const selectActiveMembers = (state: RootState): TeamMember[] => {
  const activeMembers = selectTeamMembers(state).filter((member: TeamMember) => member.isActive !== false);
  logger.log('🔍 TeamSlice Selector: selectActiveMembers returning:', activeMembers.length, 'active members');
  return activeMembers;
};

export const selectMembersByTeam = (state: RootState, teamId: string): TeamMember[] => {
  const teamMembers = selectTeamMembers(state).filter((member: TeamMember) => 
    Array.isArray(member.teams) 
      ? member.teams.includes(teamId)
      : member.teams === teamId
  );
  logger.log('🔍 TeamSlice Selector: selectMembersByTeam returning:', teamMembers.length, 'members for team:', teamId);
  return teamMembers;
};

export const selectRoleById = (state: RootState, roleId: string): Role | undefined => {
  const role = selectRoles(state).find((role: Role) => role._id === roleId);
  logger.log('🔍 TeamSlice Selector: selectRoleById for:', roleId, 'found:', !!role);
  return role;
};

export const selectRoleByName = (state: RootState, roleName: string): Role | undefined => {
  const role = selectRoles(state).find((role: Role) => role.name === roleName);
  logger.log('🔍 TeamSlice Selector: selectRoleByName for:', roleName, 'found:', !!role);
  return role;
};

// Actions
export const { clearError, setMembersPage, setTeamsPage } = teamSlice.actions;

export default teamSlice.reducer;